import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminSupportExtension,
  InsertSupportExtensionBody,
  UpdateSupportExtensionBody,
} from "./types/supportExtensions";

export const useSupportExtensionsApi = () => {
  const api = useApi();

  const useAdminSupportExtensions = useCallback(
    (query: PaginationQuery, signal: AbortSignal, systemId?: string) => {
      const mappingUrl = `/admin/settings/patrol/support_extensions`;
      const systemIdUrl = `/admin/settings/patrol/systems/${systemId}/support_extensions`;
      const url = buildUrl(systemId ? systemIdUrl : mappingUrl, query);
      return useSWR<Paginated<AdminSupportExtension>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminSupportExtension>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminSupportExtension = useCallback(
    async (body: InsertSupportExtensionBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/systems/${body.systemId}/support_extensions`;
      const id = await api.post<{ id: string }, InsertSupportExtensionBody>(url, body);
      mutateCache([
        `/admin/settings/patrol/support_extensions`,
        `/admin/settings/patrol/systems/${body.systemId}/support_extensions`,
      ]);
      return id;
    },
    [api],
  );

  const updateAdminSupportExtension = useCallback(
    async (itemId: string, body: UpdateSupportExtensionBody): Promise<void> => {
      const url = `/admin/settings/patrol/systems/${body.systemId}/support_extensions/${itemId}`;
      await api.put<UpdateSupportExtensionBody>(url, body);
      mutateCache([
        `/admin/settings/patrol/support_extensions`,
        `/admin/settings/patrol/systems/${body.systemId}/support_extensions`,
      ]);
    },
    [api],
  );

  const deleteAdminSupportExtension = useCallback(
    async (systemId: string, itemId: string): Promise<void> => {
      await api.del(`/admin/settings/patrol/systems/${systemId}/support_extensions/${itemId}`);
      mutateCache([
        `/admin/settings/patrol/support_extensions`,
        `/admin/settings/patrol/support_extensions/${itemId}`,
        `/admin/settings/patrol/systems/${systemId}/support_extensions`,
        `/admin/settings/patrol/systems/${systemId}/support_extensions/${itemId}`,
      ]);
    },
    [api],
  );

  const updateAdminSupportExtensionVisibility = useCallback(
    async (supportExtensionId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/support_extensions/${supportExtensionId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/support_extensions"]);
    },
    [api],
  );

  return {
    useAdminSupportExtensions,
    insertAdminSupportExtension,
    updateAdminSupportExtension,
    updateAdminSupportExtensionVisibility,
    deleteAdminSupportExtension,
  };
};
